import { GooglePlacesController } from "@controllers/Google-places.controller";
import { Menu, MenuItem, Stack, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useField } from "formik";
import { useEffect, useRef, useState, type FC } from "react";
import {
  FormTextField,
  type FormTextFieldProps,
} from "../form-text-field/FormTextField";

interface LocationResult {
  locationName: string;
  latLng: google.maps.LatLngLiteral;
}
interface FormGoogleAutocompleteProps extends FormTextFieldProps {
  /**
   * Returns the `locationName` and `google.maps.LatLngLiteral` object of the first result from the
   * search in `google.maps.places API` -  by the value the user has filled.
   */
  placeValue?: string;
  onLocationChange: (location: LocationResult) => void;
  isFormik?: boolean;
  outOfForm?: true;
  isRequest?: boolean; // Used to determine if the component is used in a request context
  /** Include landmarks (hospitals, stadiums, etc.) alongside street addresses. */
  includeEstablishments?: boolean;
}
const defaultLatLng = { lat: 0, lng: 0 };
export const FormGoogleAutocomplete: FC<FormGoogleAutocompleteProps> = (
  props,
) => {
  const {
    onLocationChange: setLocationValue,
    placeValue = "",
    isRequest = false,
    includeEstablishments = true,
    outOfForm,
    ...inputProps
  } = props;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [resultName, setResultName] = useState<string>();
  const [results, setResults] = useState<
    google.maps.places.AutocompletePrediction[] | null
  >(null);
  const [isSearching, setIsSearching] = useState(false);

  const [{ value = "" }, { error }] = !outOfForm ? useField<string>(inputProps.name) : [{}, {}];
  const place = useRef(placeValue);

  useEffect(() => {
    if (placeValue && !place.current) {
      place.current = placeValue;
    }
  }, [placeValue]);

  useEffect(() => {
    if (
      !place.current ||
      !value ||
      value === resultName ||
      (place.current && value === place.current) ||
      error
    ) {
      setIsSearching(false);
      return setResults(null);
    }

    setIsSearching(true);
    const timeout = setTimeout(() => {
      getLocationMutation.mutate(value);
    }, 1000);
    return () => clearTimeout(timeout);
  }, [value, place.current, error]);

  const getLocationMutation = useMutation({
    mutationKey: ["get-google-location", value],
    mutationFn: getLocation,
    onSuccess: (data) => {
      if (data) setResults(data);
    },
    onSettled: () => {
      setIsSearching(false);
    },
    gcTime: Infinity,
  });

  async function getLocation(query: string) {
    if (query && query.length >= 2) {
      return await GooglePlacesController.getAutocomplete(query, {
        includeEstablishments,
      });
    }
  }

  function removeIsraelFromLocation(description: string): string {
    // Remove ", ישראל" or "ישראל" from the end of the string
    return description.replace(/,\s*ישראל\s*$/i, "").replace(/\s*ישראל\s*$/i, "").trim();
  }

  async function handleClick(
    result: google.maps.places.AutocompletePrediction,
  ) {
    if (result.description) {
      const location = await GooglePlacesController.getPlaceCoordinates(
        result.place_id,
      );
      // Remove "ישראל" from the location name
      const locationName = removeIsraelFromLocation(result.description);
      setLocationValue({
        latLng: location ?? defaultLatLng,
        locationName,
      });
      setResults(null);
      setResultName(locationName);
    }
  }

  return (
    <>
      <FormTextField
        loading={isSearching || getLocationMutation.isPending}
        inputRef={setAnchorEl}
        value={
          isRequest ? placeValue : placeValue || place.current || value || ""
        }
        {...inputProps}
      />
      {results && results?.length > 0 && (
        <Menu
          onClose={() => setResults(null)}
          anchorEl={anchorEl}
          autoFocus={false}
          disableAutoFocusItem
          disableAutoFocus
          disableEnforceFocus
          open
        >
          {results?.map((result) => (
            <MenuItem
              sx={{ width: anchorEl?.clientWidth! + 35 }}
              key={result.place_id}
              onClick={() => handleClick(result)}
            >
              {result.place_id ? (
                <Stack
                  direction="column"
                  sx={{
                    overflow: "hidden", // Ensure stack content follows the overflow rule
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Typography
                    fontSize="medium"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {result.description}
                  </Typography>
                </Stack>
              ) : (
                <Typography
                  fontSize="medium"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {result.description}
                </Typography>
              )}
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
};
